import { toast } from 'react-toastify';
import { logoutUser } from '../custom/redux/reducers/authReducer';
import { demoPagesMenu } from '../menu';

export interface ApiError {
	message: string;
	success: boolean;
	isNetworkError?: boolean;
	statusCode?: number;
}

export interface ApiErrorHandlerProps {
	error: ApiError;
	dispatch: any;
	navigate: any;
	darkModeStatus: boolean;
	showToast?: boolean;
	customMessage?: string;
}

export const handleApiError = ({
	error,
	dispatch,
	navigate,
	darkModeStatus,
	showToast = true,
	customMessage,
}: ApiErrorHandlerProps): boolean => {
	if (error.isNetworkError) {
		if (showToast) {
			toast(customMessage || 'Connection lost. Please check your internet connection.', {
				type: 'warning',
				position: 'top-right',
				theme: darkModeStatus ? 'dark' : 'light',
				autoClose: 5000,
			});
		}
		return false;
	}

	// Handler for authentication errors (401, 403)
	if (error.statusCode === 401 || error.statusCode === 403) {
		if (showToast) {
			toast(customMessage || error.message || 'Session expired. Please login again.', {
				type: 'error',
				position: 'top-right',
				theme: darkModeStatus ? 'dark' : 'light',
			});
		}
		dispatch(logoutUser());
		navigate(`../${demoPagesMenu.login.path}`, { replace: true });
		return true;
	}

	if (showToast) {
		toast(customMessage || error.message || 'Server error occurred', {
			type: 'error',
			position: 'top-right',
			theme: darkModeStatus ? 'dark' : 'light',
		});
	}
	return false;
};

export const shouldLogoutOnError = (error: ApiError): boolean => {
	return !error.isNetworkError && (error.statusCode === 401 || error.statusCode === 403);
};

export const createApiError = (
	axiosError: any,
	defaultMessage = 'Server error occurred'
): ApiError => {
	if (!axiosError.response) {
		return {
			message: 'Network connection lost. Please check your internet connection.',
			success: false,
			isNetworkError: true,
		};
	}

	const errorData = axiosError.response.data || {};
	const statusCode = axiosError.response.status;
	
	// Check if 401/500 is actually a network/database connection issue
	// If the error message contains database connection terms, treat as network error
	const isDbConnectionError = errorData.message && (
		errorData.message.includes('EHOSTUNREACH') ||
		errorData.message.includes('ECONNREFUSED') ||
		errorData.message.includes('connect') ||
		errorData.message.includes('timeout') ||
		errorData.message.includes('ENOTFOUND')
	);
	
	if (isDbConnectionError) {
		return {
			message: 'Server connection issue. Please try again later.',
			success: false,
			isNetworkError: true,
			statusCode,
		};
	}
	
	return {
		...errorData,
		message: errorData.message || defaultMessage,
		success: false,
		isNetworkError: false,
		statusCode,
	};
};
